import { HttpError, httpErrorCode } from '@/services/http';

/**
 * Client errors that only mean « not now »: request too slow, too many requests,
 * or an app too old for the API (426, VOL-14). The queue stays on the device and
 * leaves once the app is updated, the server having applied nothing.
 */
const RETRYABLE_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 426, 429]);

export function isRetryableClientStatus(status: number): boolean {
  return RETRYABLE_CLIENT_STATUSES.has(status);
}

/**
 * Refusals that keep the queue on the device until the team is writable again
 * or the user is put back in it: they lift without any change on the tablet.
 */
export const READ_ONLY_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'TEAM_LOCKED',
  'TEAM_PAST_SEASON_READ_ONLY',
  'MATCH_ACCESS_DENIED',
]);

/** Asks the live screen to open its history, from a toast outside React. */
export const MATCH_HISTORY_REQUEST_EVENT = 'volleystats:open-match-history';

export function requestMatchHistory(): void {
  window.dispatchEvent(new Event(MATCH_HISTORY_REQUEST_EVENT));
}

/**
 * A server « no » that sending the same request again will not change: a 4xx
 * other than an expired session (401), a deleted match (404, handled apart), a
 * « not now » (408, 429) or a read-only team.
 */
export function isDefinitiveRefusal(error: unknown): boolean {
  if (!(error instanceof HttpError)) return false;
  const { status } = error;
  if (status < 400 || status >= 500) return false;
  if (status === 401 || status === 404 || isRetryableClientStatus(status)) return false;
  const code = httpErrorCode(error);
  return !(code && READ_ONLY_REFUSAL_CODES.has(code));
}
